/**
 * Which look holds which block of the look pages (ADR-094 decisions 14 and 15), with no drawing in it: the look
 * cache's pure part, which `look-cache.ts` drives and draws for.
 *
 * A **look** is named by its key (`lookKey`) and counted: every character wearing it holds it, and characters with one
 * look share one entry and so one block. Entry 0 is the fallback look, which holds block 0 for good. A **block** is a
 * numbered place on a look page; a look is drawn into its block once (a compose) and its characters then show frames
 * of it.
 *
 * Each frame every drawn character says it wants its look and how far it stands from the page's own character
 * (`want`), and is told the block to draw from: its look's once composed, the fallback's until then. The page may pin
 * a look for the frame (`pin`: the own character's and the target's). `settle` then closes the frame and plans at most
 * `composesPerFrame` composes for the caller to draw before the next frame's `want`s:
 *
 * - The fallback first, whenever it is not composed.
 * - Then the looks wanted this frame that have no composed block, pinned ones before the rest and the nearest first.
 *   Such a look takes a block never used; with none left, the block of a look not wanted this frame, the longest
 *   unwanted first; with none of those, the block of the farthest look wanted this frame, only when that look is not
 *   pinned and stands more than `evictMargin` farther than the one taking it. A pinned look takes the farthest
 *   unpinned block whatever the distances. A look that finds no block wears the fallback, and so does every farther
 *   one: past the cap the far edge of a crowd is plain.
 * - Then, after `invalidate` (a lost context), the looks that hold a block nobody wanted this frame, the most recently
 *   wanted first.
 *
 * A look whose block is taken wears the fallback from the next `want`. A look nobody holds keeps its block until it is
 * taken, so a character met again is itself at once; without a block it is forgotten and its entry used again. Only a
 * look marked ready (its sheets loaded, `setReady`) is ever planned.
 *
 * Bounded and allocation-free once made: `looks` entries and `blocks` blocks in typed arrays, a key per live entry,
 * and `want`, `pin` and `settle` allocate nothing. `settle` reads every entry once for each compose it plans and once
 * more to find there is none, and every block once for each look that needs one taken.
 */

/**
 * The sizes of a cache: its blocks (block 0 included), the looks it names at once (the fallback included), the
 * composes it plans a frame, and how much nearer a look must be to take a block drawn this frame.
 */
export interface LookBlocksOptions {
  readonly blocks: number;
  readonly looks: number;
  readonly composesPerFrame: number;
  readonly evictMargin: number;
}

/**
 * The entry of the fallback look, and its block.
 */
export const FALLBACK_LOOK = 0;

/**
 * The cache's pure part.
 */
export class LookBlocks {
  /**
   * The looks `settle` planned, by place: `jobLooks[i]` is drawn into `jobBlocks[i]`.
   */
  public readonly jobLooks: Int32Array;
  public readonly jobBlocks: Int32Array;

  /**
   * Whether the last `retain` named a look the cache did not hold, whose sheets its caller must then ask for.
   */
  public created = false;

  private readonly blocks: number;
  private readonly capacity: number;
  private readonly composesPerFrame: number;
  private readonly evictMargin: number;
  private readonly ids = new Map<string, number>();
  private readonly keys: (string | null)[];
  private readonly refs: Int32Array;
  private readonly block: Int32Array;
  private readonly isReady: Uint8Array;
  private readonly isComposed: Uint8Array;
  private readonly wanted: Int32Array;
  private readonly pinned: Int32Array;
  private readonly distance: Float64Array;
  private readonly owner: Int32Array;
  private readonly free: Int32Array;
  private freeCount = 0;
  private reached = 1;
  private fresh = 1;
  private frame = 1;
  private stale = 1;
  private count = 1;

  /**
   * @param options The cache's sizes.
   * @param fallbackKey The fallback look's key, which entry 0 holds with block 0.
   * @throws RangeError when there is no block beside the fallback's, no look beside it, no compose a frame, or a
   *   negative margin.
   */
  public constructor(options: LookBlocksOptions, fallbackKey: string) {
    if (!Number.isInteger(options.blocks) || options.blocks < 2) {
      throw new RangeError(`a look cache needs the fallback's block and one more, not ${options.blocks}`);
    }
    if (!Number.isInteger(options.looks) || options.looks < 2) {
      throw new RangeError(`a look cache names the fallback and one look more at least, not ${options.looks}`);
    }
    if (!Number.isInteger(options.composesPerFrame) || options.composesPerFrame < 1) {
      throw new RangeError(`a look cache composes one block a frame at least, not ${options.composesPerFrame}`);
    }
    if (!(options.evictMargin >= 0)) {
      throw new RangeError(`a look cache's margin is 0 or more, not ${options.evictMargin}`);
    }
    this.blocks = options.blocks;
    this.capacity = options.looks;
    this.composesPerFrame = options.composesPerFrame;
    this.evictMargin = options.evictMargin;
    this.jobLooks = new Int32Array(options.composesPerFrame);
    this.jobBlocks = new Int32Array(options.composesPerFrame);
    this.keys = new Array<string | null>(options.looks).fill(null);
    this.refs = new Int32Array(options.looks);
    this.block = new Int32Array(options.looks).fill(-1);
    this.isReady = new Uint8Array(options.looks);
    this.isComposed = new Uint8Array(options.looks);
    this.wanted = new Int32Array(options.looks);
    this.pinned = new Int32Array(options.looks);
    this.distance = new Float64Array(options.looks);
    this.owner = new Int32Array(options.blocks).fill(-1);
    this.free = new Int32Array(options.looks);
    this.keys[FALLBACK_LOOK] = fallbackKey;
    this.ids.set(fallbackKey, FALLBACK_LOOK);
    this.block[FALLBACK_LOOK] = 0;
    this.owner[0] = FALLBACK_LOOK;
  }

  /**
   * How many looks the cache names now, the fallback included.
   *
   * @returns The count, at most the `looks` it was made with.
   */
  public get live(): number {
    return this.count;
  }

  /**
   * How many blocks hold a look, the fallback's included.
   *
   * @returns The count, at most the `blocks` it was made with.
   */
  public get used(): number {
    return this.fresh;
  }

  /**
   * Takes a hold on a look for one character, naming the look when the cache does not hold it. `created` says whether
   * it was named now.
   *
   * @param key The look's key.
   * @returns The look's entry, to hand to `want`, `pin` and `release`; `FALLBACK_LOOK` when the table is full, which
   *   may be released as any other.
   */
  public retain(key: string): number {
    this.created = false;
    const known = this.ids.get(key);
    if (known !== undefined) {
      if (known !== FALLBACK_LOOK) {
        this.refs[known] = (this.refs[known] as number) + 1;
      }
      return known;
    }
    let id: number;
    if (this.freeCount > 0) {
      this.freeCount -= 1;
      id = this.free[this.freeCount] as number;
    } else if (this.reached < this.capacity) {
      id = this.reached;
      this.reached += 1;
    } else {
      return FALLBACK_LOOK;
    }
    this.keys[id] = key;
    this.ids.set(key, id);
    this.refs[id] = 1;
    this.block[id] = -1;
    this.isReady[id] = 0;
    this.isComposed[id] = 0;
    this.wanted[id] = 0;
    this.pinned[id] = 0;
    this.count += 1;
    this.created = true;
    return id;
  }

  /**
   * Lets go of one character's hold on a look. A look nobody holds is forgotten unless a block still holds it.
   *
   * @param id The look's entry, from `retain`.
   */
  public release(id: number): void {
    if (id === FALLBACK_LOOK || this.keys[id] === null || (this.refs[id] as number) <= 0) {
      return;
    }
    this.refs[id] = (this.refs[id] as number) - 1;
    if (this.refs[id] === 0 && this.block[id] === -1) {
      this.forget(id);
    }
  }

  /**
   * Forgets a look that nobody holds and no block holds, and keeps its entry for the next look named.
   *
   * @param id The look's entry.
   */
  private forget(id: number): void {
    this.ids.delete(this.keys[id] as string);
    this.keys[id] = null;
    this.free[this.freeCount] = id;
    this.freeCount += 1;
    this.count -= 1;
  }

  /**
   * Says whether a look's sheets are loaded, without which it is never composed.
   *
   * @param id The look's entry.
   * @param ready Whether every sheet it is drawn from is loaded.
   */
  public setReady(id: number, ready: boolean): void {
    this.isReady[id] = ready ? 1 : 0;
  }

  /**
   * Whether a look's sheets were said to be loaded.
   *
   * @param id The look's entry.
   * @returns True once `setReady` said so.
   */
  public ready(id: number): boolean {
    return this.isReady[id] === 1;
  }

  /**
   * Whether the cache names a look under an entry now.
   *
   * @param id The entry.
   * @returns True from the `retain` that named it until it is forgotten.
   */
  public named(id: number): boolean {
    return this.keys[id] !== null && this.keys[id] !== undefined;
  }

  /**
   * The block a look holds.
   *
   * @param id The look's entry.
   * @returns The block, composed or waiting to be; -1 when it holds none.
   */
  public blockOf(id: number): number {
    return this.block[id] as number;
  }

  /**
   * Whether a look's block shows it.
   *
   * @param id The look's entry.
   * @returns True once its block was planned by a `settle` and nothing has invalidated it since.
   */
  public composed(id: number): boolean {
    return this.isComposed[id] === 1;
  }

  /**
   * A drawn character wants its look this frame.
   *
   * @param id The look's entry.
   * @param distance How far the character stands from the page's own, in any unit `evictMargin` is in.
   * @returns The block to draw the character from: its look's once composed, 0 (the fallback's) until then.
   */
  public want(id: number, distance: number): number {
    if (this.wanted[id] !== this.frame) {
      this.wanted[id] = this.frame;
      this.distance[id] = distance;
    } else if (distance < (this.distance[id] as number)) {
      this.distance[id] = distance;
    }
    return this.isComposed[id] === 1 ? (this.block[id] as number) : 0;
  }

  /**
   * Keeps a look's block from being taken at this frame's `settle`, and puts the look before every unpinned one for a
   * block: the own character's look and the target's, said again each frame.
   *
   * @param id The look's entry.
   */
  public pin(id: number): void {
    this.pinned[id] = this.frame;
  }

  /**
   * Marks every block as showing nothing, after a lost context: each look keeps its block and wears the fallback
   * until a `settle` plans it again.
   */
  public invalidate(): void {
    this.isComposed.fill(0);
    this.stale = this.fresh;
  }

  /**
   * Closes the frame: plans up to `composesPerFrame` composes into `jobLooks` and `jobBlocks`, which the caller draws
   * before the next `want`. A planned look counts as composed from here.
   *
   * @returns How many composes were planned.
   */
  public settle(): number {
    let jobs = 0;
    while (jobs < this.composesPerFrame) {
      const look = this.nextLook();
      if (look === -1) {
        break;
      }
      let block = this.block[look] as number;
      if (block === -1) {
        block = this.blockFor(look);
        if (block === -1) {
          break;
        }
        this.block[look] = block;
        this.owner[block] = look;
      } else {
        this.stale -= 1;
      }
      this.isComposed[look] = 1;
      this.jobLooks[jobs] = look;
      this.jobBlocks[jobs] = block;
      jobs += 1;
    }
    this.frame += 1;
    return jobs;
  }

  /**
   * The look to compose next: the fallback, then the nearest wanted this frame that is ready and not composed (a
   * pinned one before any other), then, while blocks wait to be drawn again, the most recently wanted of their looks.
   *
   * @returns The look's entry, or -1 when nothing is to compose.
   */
  private nextLook(): number {
    if (this.isComposed[FALLBACK_LOOK] === 0) {
      return this.isReady[FALLBACK_LOOK] === 1 ? FALLBACK_LOOK : -1;
    }
    let best = -1;
    let bestPinned = false;
    let bestDistance = 0;
    let waiting = -1;
    for (let id = 1; id < this.reached; id += 1) {
      if (this.isComposed[id] === 1 || this.isReady[id] === 0 || this.keys[id] === null) {
        continue;
      }
      if (this.wanted[id] !== this.frame) {
        if (this.stale > 0 && this.block[id] !== -1 && (waiting === -1 || (this.wanted[id] as number) > (this.wanted[waiting] as number))) {
          waiting = id;
        }
        continue;
      }
      const pinned = this.pinned[id] === this.frame;
      const distance = this.distance[id] as number;
      if (best === -1 || (pinned && !bestPinned) || (pinned === bestPinned && distance < bestDistance)) {
        best = id;
        bestPinned = pinned;
        bestDistance = distance;
      }
    }
    return best === -1 ? waiting : best;
  }

  /**
   * A block for a look that has none: one never used, or the block of the look that should give its up. The look that
   * gave it up wears the fallback from here, and is forgotten when nobody holds it.
   *
   * @param look The entry of the look that needs a block, wanted this frame.
   * @returns The block, or -1 when no look may be evicted for this one.
   */
  private blockFor(look: number): number {
    if (this.fresh < this.blocks) {
      const block = this.fresh;
      this.fresh += 1;
      return block;
    }
    let victim = -1;
    let victimWanted = false;
    for (let block = 1; block < this.blocks; block += 1) {
      const holder = this.owner[block] as number;
      if (this.pinned[holder] === this.frame) {
        continue;
      }
      const wanted = this.wanted[holder] === this.frame;
      if (victim === -1) {
        victim = block;
        victimWanted = wanted;
        continue;
      }
      const current = this.owner[victim] as number;
      if (wanted !== victimWanted) {
        if (!wanted) {
          victim = block;
          victimWanted = false;
        }
      } else if (wanted ? (this.distance[holder] as number) > (this.distance[current] as number) : (this.wanted[holder] as number) < (this.wanted[current] as number)) {
        victim = block;
      }
    }
    if (victim === -1) {
      return -1;
    }
    const evicted = this.owner[victim] as number;
    if (victimWanted && this.pinned[look] !== this.frame && !((this.distance[evicted] as number) > (this.distance[look] as number) + this.evictMargin)) {
      return -1;
    }
    if (this.isComposed[evicted] === 0) {
      this.stale -= 1;
    }
    this.block[evicted] = -1;
    this.isComposed[evicted] = 0;
    if (this.refs[evicted] === 0) {
      this.forget(evicted);
    }
    return victim;
  }
}
